import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, ZoomIn, ZoomOut, Contrast, Link2, Type, Pause, MousePointer, BookOpen, Accessibility } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

interface A11yState {
  fontSize: number; // 0-3
  highContrast: boolean;
  grayscale: boolean;
  underlineLinks: boolean;
  readableFont: boolean;
  stopAnimations: boolean;
  bigCursor: boolean;
  readingGuide: boolean;
}

const DEFAULT_STATE: A11yState = {
  fontSize: 0,
  highContrast: false,
  grayscale: false,
  underlineLinks: false,
  readableFont: false,
  stopAnimations: false,
  bigCursor: false,
  readingGuide: false,
};

const STORAGE_KEY = 'a11y-preferences';

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<A11yState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULT_STATE, ...JSON.parse(saved) } : DEFAULT_STATE;
    } catch {
      return DEFAULT_STATE;
    }
  });
  const guideRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const { lang: language } = useLanguage();

  const labels = language === 'ar' ? {
    title: 'إمكانية الوصول',
    increaseFont: 'تكبير الخط',
    decreaseFont: 'تصغير الخط',
    highContrast: 'تباين عالٍ',
    grayscale: 'تدرج رمادي',
    underlineLinks: 'تسطير الروابط',
    readableFont: 'خط سهل القراءة',
    stopAnimations: 'إيقاف الحركات',
    bigCursor: 'مؤشر كبير',
    readingGuide: 'دليل القراءة',
    reset: 'إعادة ضبط',
    coordinator: 'منسق الوصول',
    coordinatorName: 'مجدي قاسم',
    statement: 'بيان الوصول',
  } : language === 'en' ? {
    title: 'Accessibility',
    increaseFont: 'Increase Font',
    decreaseFont: 'Decrease Font',
    highContrast: 'High Contrast',
    grayscale: 'Grayscale',
    underlineLinks: 'Underline Links',
    readableFont: 'Readable Font',
    stopAnimations: 'Stop Animations',
    bigCursor: 'Big Cursor',
    readingGuide: 'Reading Guide',
    reset: 'Reset',
    coordinator: 'Accessibility Coordinator',
    coordinatorName: 'Majdi Qasem',
    statement: 'Accessibility Statement',
  } : {
    title: 'נגישות',
    increaseFont: 'הגדלת גופן',
    decreaseFont: 'הקטנת גופן',
    highContrast: 'ניגודיות גבוהה',
    grayscale: 'גווני אפור',
    underlineLinks: 'הדגשת קישורים',
    readableFont: 'פונט קריא',
    stopAnimations: 'עצירת אנימציות',
    bigCursor: 'סמן מוגדל',
    readingGuide: 'מדריך קריאה',
    reset: 'איפוס',
    coordinator: 'רכז נגישות',
    coordinatorName: 'מג׳די קאסם',
    statement: 'הצהרת נגישות',
  };

  // Persist
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Apply effects
  useEffect(() => {
    const root = document.documentElement;
    // Font size
    const sizes = ['100%', '112.5%', '125%', '137.5%'];
    root.style.fontSize = sizes[state.fontSize] || '100%';
    // High contrast
    root.classList.toggle('a11y-high-contrast', state.highContrast);
    // Grayscale
    root.classList.toggle('a11y-grayscale', state.grayscale);
    // Underline links
    root.classList.toggle('a11y-underline-links', state.underlineLinks);
    // Readable font
    root.classList.toggle('a11y-readable-font', state.readableFont);
    // Stop animations
    root.classList.toggle('a11y-stop-animations', state.stopAnimations);
    // Big cursor
    root.classList.toggle('a11y-big-cursor', state.bigCursor);
    // Reading guide
    root.classList.toggle('a11y-reading-guide', state.readingGuide);

    return () => {
      root.style.fontSize = '';
      root.classList.remove('a11y-high-contrast', 'a11y-grayscale', 'a11y-underline-links', 'a11y-readable-font', 'a11y-stop-animations', 'a11y-big-cursor', 'a11y-reading-guide');
    };
  }, [state]);

  // Reading guide mouse tracking — write directly to DOM via rAF to avoid forced reflow
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const y = e.clientY;
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (guideRef.current) {
        guideRef.current.style.transform = `translateY(${y - 2}px)`;
      }
    });
  }, []);

  useEffect(() => {
    if (state.readingGuide) {
      window.addEventListener('mousemove', handleMouseMove, { passive: true });
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }
  }, [state.readingGuide, handleMouseMove]);

  const update = (key: keyof A11yState, value: any) => setState(prev => ({ ...prev, [key]: value }));
  const toggle = (key: keyof Omit<A11yState, 'fontSize'>) => setState(prev => ({ ...prev, [key]: !prev[key] }));

  const buttons = [
    { icon: ZoomIn, label: labels.increaseFont, action: () => update('fontSize', Math.min(3, state.fontSize + 1)), active: state.fontSize > 0 },
    { icon: ZoomOut, label: labels.decreaseFont, action: () => update('fontSize', Math.max(0, state.fontSize - 1)), active: false },
    { icon: Contrast, label: labels.highContrast, action: () => toggle('highContrast'), active: state.highContrast },
    { icon: Contrast, label: labels.grayscale, action: () => toggle('grayscale'), active: state.grayscale },
    { icon: Link2, label: labels.underlineLinks, action: () => toggle('underlineLinks'), active: state.underlineLinks },
    { icon: Type, label: labels.readableFont, action: () => toggle('readableFont'), active: state.readableFont },
    { icon: Pause, label: labels.stopAnimations, action: () => toggle('stopAnimations'), active: state.stopAnimations },
    { icon: MousePointer, label: labels.bigCursor, action: () => toggle('bigCursor'), active: state.bigCursor },
    { icon: BookOpen, label: labels.readingGuide, action: () => toggle('readingGuide'), active: state.readingGuide },
  ];

  return (
    <>
      {/* Reading guide line */}
      {state.readingGuide && (
        <div
          ref={guideRef}
          className="fixed inset-x-0 top-0 h-1 bg-primary/60 z-[9999] pointer-events-none will-change-transform"
        />
      )}

      {/* Toggle button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed start-6 bottom-24 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label={labels.title}
        title={labels.title}
      >
        <Accessibility className="w-7 h-7" />
      </button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.2 }}
              className="fixed start-4 bottom-20 z-50 w-80 max-h-[80vh] overflow-y-auto rounded-2xl bg-card border border-border shadow-2xl p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                  <Accessibility className="w-5 h-5 text-primary" />
                  {labels.title}
                </h2>
                <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                {buttons.map((btn, i) => (
                  <button
                    key={i}
                    onClick={btn.action}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl text-xs transition-colors border ${
                      btn.active
                        ? 'bg-primary/20 border-primary text-primary'
                        : 'bg-muted/30 border-transparent text-muted-foreground hover:bg-muted/60'
                    }`}
                  >
                    <btn.icon className="w-5 h-5" />
                    <span className="text-center leading-tight">{btn.label}</span>
                  </button>
                ))}
              </div>

              {/* Reset */}
              <button
                onClick={() => setState(DEFAULT_STATE)}
                className="w-full flex items-center justify-center gap-2 p-2 rounded-xl bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors text-sm font-medium mb-4"
              >
                <RotateCcw className="w-4 h-4" />
                {labels.reset}
              </button>

              {/* Accessibility statement link */}
              <div className="border-t border-border pt-3 text-sm">
                <a
                  href={`/${language}/accessibility`}
                  className="inline-block text-primary hover:underline text-sm"
                >
                  {labels.statement} →
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default AccessibilityWidget;
